import React, { useState, useEffect } from 'react';
import {
  Card, Typography, Upload, Button, Table, Image, Space,
  message, Row, Col, Statistic, Alert, Progress, Popconfirm,
  Tag, Collapse, Tabs, Modal, Spin, Select
} from 'antd';
import {
  PictureOutlined, UploadOutlined, DeleteOutlined,
  CheckCircleOutlined, CloseCircleOutlined, InboxOutlined,
  ReloadOutlined, LoadingOutlined
} from '@ant-design/icons';
import { imagenService, animalService, eventoService, fixUrl } from '../../services/api';
import { eventosDeListaCorta } from '../../utils/listaCorta';
import { portadaDe } from '../../utils/portadaAnimal';
import PresentacionImagen from '../../components/PresentacionImagen';
import VideosTab from './VideosTab';

const { Title, Text, Paragraph } = Typography;
const { Dragger } = Upload;
const { Option } = Select;

// Fotos por request. Se manda en tandas por dos motivos: el servidor genera
// dos versiones de cada imagen (1200px + 400px) y tiene 120s de `gunicorn
// --timeout`; y una seleccion completa puede pesar cientos de MB, que a una
// subida hogareña no entra en una sola request. Con tandas, cada una cierra
// rapido y lo cargado queda firme aunque una tanda falle.
const TANDA_FOTOS = 25;
// Tope de PESO por tanda, además del de cantidad. Cortar solo por cantidad
// dejaba el peso de la request librado al tamaño de las fotos: con fotos de
// cámara de ~4,7 MB (San Alejo, 2026-10-13) una tanda de 25 son ~118 MB, que
// con la calibración de abajo (0,6 s/foto + 1,1 MB/s) tarda ~134 s, más que
// los 120 s de `gunicorn --timeout`. El worker muere antes de terminar de
// recibir el multipart, no queda NADA en disco y la tanda entera falla. Con
// 60 MB la tanda tarda ~70 s al ancho de banda medido y sigue entrando aunque
// la conexión rinda la mitad. Achicar la tanda no alarga la carga total (es
// el mismo peso repartido en más requests), solo achica lo que se pierde si
// una falla.
const TANDA_MB_MAX = 60;

// Arma las tandas: cierra una cuando llega a `TANDA_FOTOS` archivos O cuando
// el siguiente la pasaría de `TANDA_MB_MAX`. Un archivo solo siempre va,
// aunque pese más que el tope (el Dragger ya rechaza los de más de 10 MB).
const armarTandas = (archivos) => {
  const tandas = [];
  let actual = [];
  let pesoActual = 0;
  archivos.forEach((f) => {
    const peso = (f.size || 0) / (1024 * 1024);
    const cierra = actual.length >= TANDA_FOTOS
      || (actual.length > 0 && pesoActual + peso > TANDA_MB_MAX);
    if (cierra) {
      tandas.push(actual);
      actual = [];
      pesoActual = 0;
    }
    actual.push(f);
    pesoActual += peso;
  });
  if (actual.length) tandas.push(actual);
  return tandas;
};

// Calibrado con cargas reales (2026-08-13): fotos de ~1,7 MB y 3543x2395, en
// tandas de ~30. La primera version se quedaba 5-10s corta, asi que se subio
// el costo por foto y se bajo el ancho de banda asumido. Preferimos que el
// estimado sobre y no que falte: un cartel que promete menos de lo que tarda
// hace pensar que la carga se colgo.
const SEG_PROCESO_POR_FOTO = 0.6;
const MB_POR_SEGUNDO_SUBIDA = 1.1;
const MARGEN = 1.1;

const estimarSegundosFotos = (archivos) => {
  if (!archivos || archivos.length === 0) return null;
  const mb = archivos.reduce((acc, f) => acc + (f.size || 0), 0) / (1024 * 1024);
  const proceso = archivos.length * SEG_PROCESO_POR_FOTO;
  const subida = mb / MB_POR_SEGUNDO_SUBIDA;
  return Math.max(5, Math.ceil((proceso + subida) * MARGEN));
};

const formatTiempo = (s) => {
  const m = Math.floor(s / 60);
  const ss = s % 60;
  return m > 0 ? `${m}m ${ss.toString().padStart(2, '0')}s` : `${ss}s`;
};

// Botón de carga directa para la fila "sin imagen". Va a nivel de módulo (y
// no inline en la columna) porque tiene su propio estado de `loading` por
// fila: un botón compartido quedaría en `loading` para TODAS las filas
// mientras sube una sola.
//
// `beforeUpload` se dispara UNA VEZ POR ARCHIVO, pero su segundo argumento ya
// trae la selección COMPLETA — mismo idiom que `AnimalForm.handleUploadImagen`.
// Se actúa solo en la primera llamada, con la tanda entera, y se devuelve
// `false` siempre para que antd nunca intente su propio POST.
//
// No se valida tipo/tamaño del lado del cliente: `imagenService.uploadAnimal`
// ya vuelve con `rechazadas` para lo que el backend no acepta (mismo contrato
// que usa la ficha), así que duplicar la validación aquí solo agrega una forma
// más de que un archivo invisible se trabe sin mandar el resto de la tanda.
const UploadFotoDirecta = ({ idAnimal, onSubida }) => {
  const [subiendo, setSubiendo] = useState(false);

  const subir = async (tanda) => {
    setSubiendo(true);
    try {
      const res = await imagenService.uploadAnimal(idAnimal, tanda);
      const cuantas = res.data?.total ?? tanda.length;
      message.success(cuantas === 1 ? 'Foto cargada' : `${cuantas} fotos cargadas`);
      const rechazadas = res.data?.rechazadas || [];
      if (rechazadas.length) {
        const nombres = rechazadas.map((r) => r.archivo).join(', ');
        message.warning(rechazadas.length === 1
          ? `1 no se pudo subir: ${nombres}`
          : `${rechazadas.length} no se pudieron subir: ${nombres}`);
      }
      onSubida(idAnimal);
    } catch (err) {
      message.error(err.response?.data?.error || 'Error al subir la foto');
    } finally {
      setSubiendo(false);
    }
  };

  const beforeUpload = (file, fileList) => {
    const tanda = fileList && fileList.length ? fileList : [file];
    if (tanda.indexOf(file) === 0) {
      subir(tanda);
    }
    return false;
  };

  return (
    <Upload multiple accept="image/*" showUploadList={false} beforeUpload={beforeUpload}>
      <Button size="small" icon={<UploadOutlined />} loading={subiendo}>
        Cargar foto
      </Button>
    </Upload>
  );
};

// Columnas de la tabla de animales filtrados (con/sin imagen). Función de
// módulo y no un array fijo: la columna "Foto" solo tiene sentido con
// `filtroImagen === 'con'` (no hay nada que mostrar en "sin imagen") y
// "Acciones" solo con `'sin'` (en "con" no hay nada que cargar).
const columnasAnimalesFiltrados = (filtroImagen, onSubida) => {
  const columnas = [];

  if (filtroImagen === 'con') {
    columnas.push({
      title: 'Foto',
      key: 'foto',
      width: 80,
      render: (_, record) => {
        // Misma regla que el resto del panel y que el micrositio público:
        // la marcada `es_portada`, o la primera si nadie marcó ninguna.
        const portada = portadaDe(record.imagenes);
        return portada ? (
          <Image
            src={fixUrl(portada.url_thumb || portada.url_imagen)}
            width={50}
            height={50}
            style={{ objectFit: 'cover', borderRadius: 4 }}
          />
        ) : '-';
      },
    });
  }

  columnas.push(
    {
      title: 'RP',
      dataIndex: 'rp_animal',
      key: 'rp_animal',
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    { title: 'Nombre', dataIndex: 'nom_animal', key: 'nom_animal' },
    {
      title: 'Corral',
      dataIndex: 'corral',
      key: 'corral',
      render: (v) => v ?? '-',
    },
    {
      title: 'Categoría',
      key: 'categoria',
      render: (_, record) => record.categoria?.nom_categoria || '-',
    },
    {
      title: 'Raza',
      dataIndex: 'nom_raza',
      key: 'nom_raza',
      render: (v) => v || '-',
    },
  );

  if (filtroImagen === 'sin') {
    columnas.push({
      title: 'Acciones',
      key: 'acciones',
      width: 140,
      render: (_, record) => (
        <UploadFotoDirecta idAnimal={record.id_animal} onSubida={onSubida} />
      ),
    });
  }

  return columnas;
};

const FotosVideos = () => {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [imagenes, setImagenes] = useState([]);
  const [estadisticas, setEstadisticas] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [resultadosCarga, setResultadosCarga] = useState(null);
  const [tandaActual, setTandaActual] = useState(0);
  const [totalTandas, setTotalTandas] = useState(0);
  const [tiempoTranscurrido, setTiempoTranscurrido] = useState(0);

  // Filtro "con imagen" / "sin imagen" activado desde las cards de
  // estadísticas. `null` = sin filtro, la pantalla queda como siempre
  // (tabla de IMÁGENES). Con un valor, se muestra la tabla de ANIMALES.
  const [filtroImagen, setFiltroImagen] = useState(null);
  const [animalesFiltrados, setAnimalesFiltrados] = useState([]);
  const [loadingAnimales, setLoadingAnimales] = useState(false);
  const [paginacionAnimales, setPaginacionAnimales] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  // Remate para acotar la tabla de animales filtrados: el productor tiene un
  // remate encima y quiere ver qué le falta cargar DE ESE remate, no del
  // histórico completo.
  const [eventoFiltro, setEventoFiltro] = useState(undefined);
  const [eventosOpciones, setEventosOpciones] = useState([]);
  const [loadingEventos, setLoadingEventos] = useState(false);

  // Cronometro del modal: corre mientras dura la carga (todas las tandas).
  useEffect(() => {
    if (!uploading) {
      setTiempoTranscurrido(0);
      setTandaActual(0);
      setTotalTandas(0);
      return;
    }
    const interval = setInterval(() => {
      setTiempoTranscurrido((s) => s + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [uploading]);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const segundosEstimados = estimarSegundosFotos(fileList);

  // Estadísticas y remates solo al mount (no dependen de la página). Los
  // remates se piden siempre y no solo al activar un filtro: es la misma
  // lista liviana que usan los demás selectores del panel, y pedirla recién
  // al primer click del productor metería un salto de `loading` en el Select.
  useEffect(() => {
    fetchEstadisticas();
    fetchEventosOpciones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Imágenes al mount Y cada vez que cambia la página
  // (antes había dos useEffect que disparaban fetchImagenes en paralelo al mount)
  useEffect(() => {
    fetchImagenes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize]);

  // Tabla de animales filtrados: solo corre con un filtro activo. Cambiar el
  // remate o el filtro reinicia la página (ver `toggleFiltroImagen` y
  // `handleCambiarEventoFiltro`), así que esto no necesita hacerlo de nuevo.
  useEffect(() => {
    if (!filtroImagen) return;
    fetchAnimalesFiltrados();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroImagen, eventoFiltro, paginacionAnimales.current, paginacionAnimales.pageSize]);

  const fetchImagenes = async () => {
    setLoading(true);
    try {
      const response = await imagenService.getAll({
        page: pagination.current,
        per_page: pagination.pageSize,
      });
      setImagenes(response.data?.imagenes || []);
      setPagination(prev => ({
        ...prev,
        total: response.data?.total || 0,
      }));
    } catch (error) {
      console.error('Error al cargar imágenes:', error);
      message.error(error.response?.data?.error || 'Error al cargar imágenes');
    } finally {
      setLoading(false);
    }
  };

  const fetchEstadisticas = async () => {
    try {
      const response = await imagenService.getEstadisticas();
      setEstadisticas(response.data);
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
      message.error(error.response?.data?.error || 'Error al cargar estadísticas');
    }
  };

  const fetchEventosOpciones = async () => {
    setLoadingEventos(true);
    try {
      const response = await eventoService.getListaCorta();
      setEventosOpciones(eventosDeListaCorta(response));
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al cargar los remates');
    } finally {
      setLoadingEventos(false);
    }
  };

  // Tabla de ANIMALES (no de imágenes) con/sin foto, filtrada opcionalmente
  // por remate. `animalService.getAll` ya acepta `imagen` como filtro más
  // (GET /animales/?imagen=con|sin, backend/app/routes/animal_routes.py).
  const fetchAnimalesFiltrados = async () => {
    setLoadingAnimales(true);
    try {
      const response = await animalService.getAll({
        imagen: filtroImagen,
        evento: eventoFiltro,
        page: paginacionAnimales.current,
        per_page: paginacionAnimales.pageSize,
      });
      setAnimalesFiltrados(response.data?.animales || []);
      setPaginacionAnimales((prev) => ({
        ...prev,
        total: response.data?.total || 0,
      }));
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al cargar los animales');
    } finally {
      setLoadingAnimales(false);
    }
  };

  // Click en la card ya elegida = deselecciona (vuelve a la tabla de
  // imágenes de siempre). Cambiar de filtro siempre arranca en página 1: la
  // paginación de "con imagen" no tiene por qué coincidir con la de "sin".
  const toggleFiltroImagen = (valor) => {
    setFiltroImagen((prev) => (prev === valor ? null : valor));
    setPaginacionAnimales((prev) => ({ ...prev, current: 1 }));
  };

  const handleCambiarEventoFiltro = (valor) => {
    setEventoFiltro(valor);
    setPaginacionAnimales((prev) => ({ ...prev, current: 1 }));
  };

  const handleTableChangeAnimales = (pag) => {
    setPaginacionAnimales((prev) => ({
      ...prev,
      current: pag.current,
      pageSize: pag.pageSize,
    }));
  };

  // Una foto recién subida saca al animal de la lista de "sin imagen" sin
  // esperar un refetch completo (la fila ya cumplió lo que la trajo a esta
  // tabla), y refresca las estadísticas de arriba para que el contador
  // "Sin Imagen" baje en el acto.
  const quitarDeSinImagen = (idAnimal) => {
    setAnimalesFiltrados((prev) => prev.filter((a) => a.id_animal !== idAnimal));
    setPaginacionAnimales((prev) => ({ ...prev, total: Math.max(0, prev.total - 1) }));
    fetchEstadisticas();
  };

  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.warning('Seleccione al menos un archivo');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setResultadosCarga(null);

    // Las fotos se mandan en tandas. Se ordenan por nombre para que la
    // principal de cada animal (6384.jpg) viaje antes que sus adicionales
    // (6384_2.jpg): el micrositio usa como portada la imagen de menor
    // id_imagen, o sea la primera insertada.
    // La extension se saca ANTES de comparar: en la colacion ICU el "_" pesa
    // menos que el ".", asi que comparando nombres completos "6384_2.jpg"
    // queda ANTES que "6384.jpg" y la foto adicional terminaba de portada.
    // Sin extension "6384" es prefijo de "6384_2" y gana el mas corto.
    const claveOrden = (n) => String(n).replace(/\.[^.]+$/, '');
    const archivos = [...fileList].sort((a, b) =>
      claveOrden(a.name).localeCompare(claveOrden(b.name), 'es', { numeric: true })
    );
    const tandas = armarTandas(archivos);
    setTotalTandas(tandas.length);

    const acumulado = {
      exitosos: [], errores: [],
      total_procesados: 0, total_exitosos: 0, total_errores: 0,
    };
    const tandasFallidas = [];

    try {
      for (let t = 0; t < tandas.length; t++) {
        setTandaActual(t + 1);
        try {
          const response = await imagenService.uploadBulk(
            tandas[t],
            (progressEvent) => {
              const pctTanda = progressEvent.total
                ? progressEvent.loaded / progressEvent.total
                : 0;
              setUploadProgress(
                Math.round(((t + pctTanda) / tandas.length) * 100)
              );
            }
          );
          const d = response.data || {};
          acumulado.exitosos.push(...(d.exitosos || []));
          acumulado.errores.push(...(d.errores || []));
          acumulado.total_procesados += d.total_procesados || 0;
          acumulado.total_exitosos += d.total_exitosos || 0;
          acumulado.total_errores += d.total_errores || 0;
        } catch (errTanda) {
          // Una tanda que falla no tiene por que tumbar las demas: se marcan
          // sus archivos como error y se sigue con la siguiente. El error se
          // acumula y se reporta con UN solo message.error al terminar el
          // loop: un toast por tanda taparia la pantalla.
          const detalle = errTanda.response?.data?.error || errTanda.message;
          tandasFallidas.push(`tanda ${t + 1} (${detalle})`);
          tandas[t].forEach((f) => {
            acumulado.errores.push({
              archivo: f.name,
              // El nombre del archivo ES el RP (con sufijo opcional _N), asi
              // que la columna "RP Buscado" de la tabla no queda vacia.
              rp_buscado: f.name.replace(/\.[^.]+$/, ''),
              error: `Tanda ${t + 1} de ${tandas.length} falló: ${detalle}`,
            });
            acumulado.total_errores += 1;
            acumulado.total_procesados += 1;
          });
        }
      }

      setResultadosCarga(acumulado);
      setFileList([]);
      fetchImagenes();
      fetchEstadisticas();

      if (acumulado.total_exitosos > 0) {
        message.success(`${acumulado.total_exitosos} imagen(es) cargada(s) exitosamente`);
      }
      if (acumulado.total_errores > 0) {
        message.warning(`${acumulado.total_errores} imagen(es) con errores`);
      }
      if (tandasFallidas.length > 0) {
        message.error(
          `Falló ${tandasFallidas.length} de ${tandas.length} tanda(s): ` +
          `${tandasFallidas.join('; ')}. Revisá el detalle y volvé a subir solo esas fotos.`
        );
      }
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al cargar las imágenes');
      console.error(error);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (id) => {
    try {
      await imagenService.delete(id);
      message.success('Imagen eliminada');
      fetchImagenes();
      fetchEstadisticas();
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al eliminar');
    }
  };

  const handleTableChange = (pag) => {
    setPagination(prev => ({
      ...prev,
      current: pag.current,
      pageSize: pag.pageSize,
    }));
  };

  const uploadProps = {
    multiple: true,
    fileList,
    onRemove: (file) => {
      setFileList(prev => prev.filter(f => f.uid !== file.uid));
    },
    beforeUpload: (file) => {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error(`${file.name} no es una imagen válida`);
        return false;
      }
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error(`${file.name} excede el tamaño máximo de 10MB`);
        return false;
      }
      setFileList(prev => [...prev, file]);
      return false; // Prevenir upload automático
    },
    accept: 'image/*',
  };

  const columns = [
    {
      title: 'Imagen',
      dataIndex: 'url_imagen',
      key: 'imagen',
      width: 100,
      render: (url) => (
        <Image 
          src={fixUrl(url)}
          width={60} 
          height={60} 
          style={{ objectFit: 'cover', borderRadius: 4 }}
        />
      ),
    },
    {
      title: 'Animal',
      dataIndex: 'nom_imagen',
      key: 'nom_imagen',
    },
    {
      title: 'RP',
      dataIndex: 'rp_animal_img',
      key: 'rp_animal_img',
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: 'Dimensiones',
      key: 'dimensiones',
      render: (_, record) => (
        record.ancho_imagen && record.alto_imagen 
          ? `${record.ancho_imagen} x ${record.alto_imagen}` 
          : '-'
      ),
    },
    {
      title: 'Presentación',
      key: 'presentacion',
      width: 240,
      // LOS TRES CONTROLES JUNTOS y guardando solos, sin un botón de "guardar"
      // aparte: los tres cambian cómo se USA una foto que ya existe y ninguno
      // toca el archivo. Un formulario con botón para tres toggles agrega un
      // paso que no decide nada.
      render: (_, record) => (
        <PresentacionImagen
          imagen={record}
          // La fila se reemplaza con lo que devolvió el backend y no con lo que
          // mandamos: al marcar una portada el servidor DESMARCA la anterior, y
          // esa otra fila también hay que refrescarla.
          onCambio={(actualizada) => {
            setImagenes((prev) => prev.map((img) => {
              if (img.id_imagen === actualizada.id_imagen) return actualizada;
              // Si la que cambió pasó a ser portada, la que lo era de ESE animal
              // dejó de serlo — el backend ya la desmarcó en la base.
              if (actualizada.es_portada
                  && img.id_animal === actualizada.id_animal
                  && img.es_portada) {
                return { ...img, es_portada: 0 };
              }
              return img;
            }));
          }}
        />
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Popconfirm
          title="¿Eliminar esta imagen?"
          onConfirm={() => handleDelete(record.id_imagen)}
          okText="Sí"
          cancelText="No"
        >
          <Button type="text" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  const tabFotos = (
    <>
      {/* Estadísticas */}
      {estadisticas && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic
                title="Total Imágenes"
                value={estadisticas.total_imagenes}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic
                title="Total Animales"
                value={estadisticas.total_animales}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            {/* Cliqueable: filtra el listado de animales de abajo por los
                que SÍ tienen al menos una foto. Un segundo click sobre la
                misma card la deselecciona (vuelve a la tabla de imágenes). */}
            <Card
              hoverable
              onClick={() => toggleFiltroImagen('con')}
              style={{
                cursor: 'pointer',
                borderColor: filtroImagen === 'con' ? '#52c41a' : undefined,
                borderWidth: filtroImagen === 'con' ? 2 : 1,
              }}
            >
              <Statistic
                title="Con Imagen"
                value={estadisticas.animales_con_imagen}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            {/* Misma idea que "Con Imagen", pero además es la puerta de
                entrada para cargar las fotos que faltan sin ir animal por
                animal. */}
            <Card
              hoverable
              onClick={() => toggleFiltroImagen('sin')}
              style={{
                cursor: 'pointer',
                borderColor: filtroImagen === 'sin' ? '#faad14' : undefined,
                borderWidth: filtroImagen === 'sin' ? 2 : 1,
              }}
            >
              <Statistic
                title="Sin Imagen"
                value={estadisticas.animales_sin_imagen}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Zona de carga */}
      <Card title="Subir Imágenes" style={{ marginBottom: 24 }}>
        <Alert
          message="Instrucciones de carga"
          description={
            <div>
              <Paragraph style={{ marginBottom: 8 }}>
                <strong>El nombre del archivo debe coincidir con el RP del animal.</strong>
              </Paragraph>
              <ul style={{ marginBottom: 0 }}>
                <li>Ejemplo: Si el RP es <Tag>ABC123</Tag>, el archivo debe llamarse <Tag>ABC123.jpg</Tag></li>
                <li>Formatos permitidos: JPG, JPEG, PNG, GIF, WEBP</li>
                <li>Tamaño máximo por archivo: 10MB</li>
                <li>Puede seleccionar múltiples archivos a la vez</li>
              </ul>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Dragger {...uploadProps} style={{ marginBottom: 16 }}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Haga clic o arrastre archivos a esta área para subirlos
          </p>
          <p className="ant-upload-hint">
            Seleccione múltiples imágenes. El nombre debe coincidir con el RP del animal.
          </p>
        </Dragger>

        {fileList.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <Text strong>{fileList.length} archivo(s) seleccionado(s)</Text>
          </div>
        )}

        {uploading && (
          <Progress percent={uploadProgress} status="active" style={{ marginBottom: 16 }} />
        )}

        <Space>
          <Button
            type="primary"
            icon={<UploadOutlined />}
            onClick={handleUpload}
            loading={uploading}
            disabled={fileList.length === 0}
          >
            {uploading ? 'Subiendo...' : `Subir ${fileList.length} archivo(s)`}
          </Button>
          {fileList.length > 0 && (
            <Button onClick={() => setFileList([])}>
              Limpiar selección
            </Button>
          )}
        </Space>
      </Card>

      {/* Resultados de la carga */}
      {resultadosCarga && (
        <Card title="Resultado de la carga" style={{ marginBottom: 24 }}>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={12} md={8}>
              <Statistic
                title="Procesados"
                value={resultadosCarga.total_procesados}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Statistic
                title="Exitosos"
                value={resultadosCarga.total_exitosos}
                valueStyle={{ color: '#52c41a' }}
                prefix={<CheckCircleOutlined />}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Statistic
                title="Errores"
                value={resultadosCarga.total_errores}
                valueStyle={{ color: '#ff4d4f' }}
                prefix={<CloseCircleOutlined />}
              />
            </Col>
          </Row>

          <Collapse ghost items={[
            resultadosCarga.exitosos?.length > 0 && {
              key: 'exitosos',
              label: <Text type="success">Ver archivos exitosos ({resultadosCarga.exitosos.length})</Text>,
              children: (
                <Table
                  scroll={{ x: 'max-content' }}
                  size="small"
                  dataSource={resultadosCarga.exitosos}
                  rowKey="id_imagen"
                  pagination={false}
                  columns={[
                    { title: 'Archivo', dataIndex: 'archivo', key: 'archivo' },
                    { title: 'RP', dataIndex: 'rp_animal', key: 'rp' },
                    { title: 'Animal', dataIndex: 'animal', key: 'animal' },
                    { title: 'Dimensiones', dataIndex: 'dimensiones', key: 'dim' },
                  ]}
                />
              ),
            },
            resultadosCarga.errores?.length > 0 && {
              key: 'errores',
              label: <Text type="danger">Ver errores ({resultadosCarga.errores.length})</Text>,
              children: (
                <Table
                  scroll={{ x: 'max-content' }}
                  size="small"
                  dataSource={resultadosCarga.errores}
                  // El nombre solo no alcanza como key: la seleccion se dedupea
                  // por uid, asi que dos carpetas distintas pueden aportar un
                  // "6384.jpg" cada una y las keys colisionarian.
                  rowKey={(r, i) => `${r.archivo}-${i}`}
                  pagination={false}
                  columns={[
                    { title: 'Archivo', dataIndex: 'archivo', key: 'archivo' },
                    { title: 'RP Buscado', dataIndex: 'rp_buscado', key: 'rp' },
                    { title: 'Error', dataIndex: 'error', key: 'error',
                      render: (text) => <Text type="danger">{text}</Text>
                    },
                  ]}
                />
              ),
            },
          ].filter(Boolean)} />
        </Card>
      )}

      {/* Con un filtro de "con/sin imagen" activo, esto deja de ser la
          tabla de IMÁGENES de siempre y pasa a ser la tabla de ANIMALES que
          corresponde al filtro elegido — es lo que pidió el productor: ver
          esos animales abajo, y en "sin imagen" poder cargarles la foto ahí
          mismo. Sin filtro, la pantalla queda EXACTAMENTE como antes. */}
      {filtroImagen ? (
        <Card
          title={filtroImagen === 'con' ? 'Animales con imagen' : 'Animales sin imagen'}
          extra={
            <Space>
              <Select
                placeholder="Todos los remates"
                style={{ width: 220 }}
                loading={loadingEventos}
                allowClear
                value={eventoFiltro}
                onChange={handleCambiarEventoFiltro}
              >
                {eventosOpciones.map((ev) => (
                  <Option key={ev.id_evento} value={ev.id_evento}>
                    {ev.nombre_evento}{ev.es_finalizado ? ' (finalizado)' : ''}
                  </Option>
                ))}
              </Select>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => { fetchAnimalesFiltrados(); fetchEstadisticas(); }}
              >
                Actualizar
              </Button>
            </Space>
          }
        >
          <Table
            scroll={{ x: 'max-content' }}
            columns={columnasAnimalesFiltrados(filtroImagen, quitarDeSinImagen)}
            dataSource={animalesFiltrados}
            rowKey="id_animal"
            loading={loadingAnimales}
            pagination={{
              ...paginacionAnimales,
              showSizeChanger: true,
              showTotal: (total) => `Total: ${total} animales`,
            }}
            onChange={handleTableChangeAnimales}
          />
        </Card>
      ) : (
        <Card
          title="Imágenes Cargadas"
          extra={
            <Button
              icon={<ReloadOutlined />}
              onClick={() => { fetchImagenes(); fetchEstadisticas(); }}
            >
              Actualizar
            </Button>
          }
        >
          <Table
            scroll={{ x: 'max-content' }}
            columns={columns}
            dataSource={imagenes}
            rowKey="id_imagen"
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showTotal: (total) => `Total: ${total} imágenes`,
            }}
            onChange={handleTableChange}
          />
        </Card>
      )}
    </>
  );

  return (
    <div>
      <Title level={2}>
        <PictureOutlined /> Fotos y Videos
      </Title>
      <Tabs
        items={[
          { key: 'fotos', label: 'Fotos', children: tabFotos },
          { key: 'videos', label: 'Videos', children: <VideosTab /> },
        ]}
      />

      {/* Modal de progreso: se abre al arrancar y se cierra solo cuando
          terminaron TODAS las tandas. El estimado sale de la cantidad y el
          peso real de los archivos seleccionados. */}
      <Modal
        open={uploading}
        closable={false}
        maskClosable={false}
        keyboard={false}
        footer={null}
        width={460}
        centered
      >
        <div style={{ textAlign: 'center', padding: '12px 0' }}>
          <Spin
            size="large"
            indicator={<LoadingOutlined style={{ fontSize: 40, color: '#1890ff' }} spin />}
          />
          <Title level={4} style={{ marginTop: 20, marginBottom: 6 }}>
            Subiendo fotos
          </Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 14 }}>
            {totalTandas > 1
              ? <>Tanda <strong>{tandaActual}</strong> de <strong>{totalTandas}</strong>. </>
              : null}
            {segundosEstimados
              ? <>Deberia tardar unos <strong>{formatTiempo(segundosEstimados)}</strong>.</>
              : 'Procesando las imagenes.'}
          </Text>

          <div style={{ marginBottom: 14 }}>
            <Progress percent={uploadProgress} size="small" />
          </div>

          <Text strong style={{ display: 'block', marginBottom: 18, fontSize: 18, color: '#1890ff' }}>
            ⏱ {formatTiempo(tiempoTranscurrido)}
          </Text>

          {segundosEstimados && tiempoTranscurrido > segundosEstimados * 3 && (
            <Alert
              type="info"
              showIcon
              message="Esta tardando mas de lo previsto"
              description="Puede ser la velocidad de subida. Las tandas ya completadas quedaron guardadas."
              style={{ textAlign: 'left', marginBottom: 12 }}
            />
          )}

          <Alert
            type="warning"
            showIcon
            message="No cierres ni recargues esta pestaña"
            description="Las tandas ya subidas quedan guardadas, pero las que falten se cancelan."
            style={{ textAlign: 'left' }}
          />
        </div>
      </Modal>
    </div>
  );
};

export default FotosVideos;
